import mvtMapStatic from '@/common/components/mvtmapstatic/mvt-mapstatic.vue';
import mvtCardPropertyPagination from './mvt-cardproperty-pagination.vue';
import { mapGetters, mapActions } from 'vuex';
import throttle from 'lodash/throttle';
let observerCardProperty;
const ImgStatus = {
    UNLOAD: 0,
    LOADING: 1,
    LOADED: 2,
};
//get the status from the statusUrl
function getImgStatus(url) {
    if (!url) {
        return ImgStatus.UNLOAD;
    }
    if (url.startsWith(`${ImgStatus.UNLOAD}_`)) {
        return ImgStatus.UNLOAD;
    } else if (url.startsWith(`${ImgStatus.LOADING}_`)) {
        return ImgStatus.LOADING;
    } else {
        return ImgStatus.LOADED;
    }
}
//get the url with status prefix
//if unload, img url will be like 0_xxx.jpg
//if loading, img url will be like 1_xxx.jpg
//default will be xxx.jpg
function getImgStatusUrl(url, currentStatus) {
    if (!url) {
        return url;
    }
    if (currentStatus === ImgStatus.UNLOAD) {
        return url.replace(/^(\d_)?/, `${ImgStatus.UNLOAD}_`);
    } else if (currentStatus === ImgStatus.LOADING) {
        return url.replace(/^(\d_)?/, `${ImgStatus.LOADING}_`);
    } else if (/^\d_/.test(url)) {
        return url.replace(/^\d_/, '');
    }
    return url;
}

export default {
    name: 'PropertyCardImage',
    props: ['property', 'showStreetView', 'staticMapData', 'theme', 'previewPhoto', 'cardParent'],
    inject: ['$eventBus'],
    components: {
        mvtMapStatic,
        mvtCardPropertyPagination,
    },
    computed: {
        ...mapGetters('glb', ['glb', 'getSplit']),
        isLoadingAnimation() {
            return this.photoUrls && this.photoUrls.length > 0 && getImgStatus(this.photoUrls[this.index]) !== ImgStatus.LOADED;
        },
        photoUrl() {
            if (this.photoUrls.length <= 1) {
                if (this.cardParent === 'carousel' && this.getSplit('movoto-property-carousel-image-CW-4295')) {
                    return this.property.tnImgPath?.replace(/(_t|_p)\.(jpeg|webp)$/, '_m.jpeg');
                }
                return this.property.tnImgPath;
            }
            let url = this.photoUrls[this.index];
            let loaded = getImgStatus(url) === ImgStatus.LOADED;
            if (!url || !loaded) {
                return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
            }
            return url;
        },
        nextPhotoUrl() {
            if (this.photoUrls.length <= 1) {
                return null;
            }
            let url = this.photoUrls[this.prevIndex];
            let loaded = getImgStatus(url) === ImgStatus.LOADED;
            if (!url || !loaded) {
                return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
            }
            return url;
        },
        prevPhotoUrl() {
            if (this.photoUrls.length <= 1) {
                return null;
            }
            let url = this.photoUrls[this.nextIndex];
            let loaded = getImgStatus(url) === ImgStatus.LOADED;
            if (!url || !loaded) {
                return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
            }
            return url;
        },
        showPreview() {
            return this.loaded && this.property.photoCount > 1 && this.previewPhoto;
        },
        getPhotoCount() {
            if (this.photoUrls.length > 0) {
                return this.photoUrls.length;
            }
            return this.property.photoCount;
        },
        disablePreviewPhoto() {
            return this.getPhotoCount <= 1 || !this.previewPhoto;
        },
    },
    data() {
        return {
            index: 0,
            viewedCount: 0,
            prevIndex: this.property.photoCount - 1,
            nextIndex: 1,
            photoUrls: [],
            loading: 0,
            loadingAnimation: 0,
            isFocusMode: false,
            loaded: false,
            animationName: '',
            initaledDrag: false,
            hoverTimer: null,
            sensitivity: 20,
            buffer: 2,
            timer: null,
            // Event handler references for cleanup
            eventHandlers: {
                touchstart: null,
                touchmove: null,
                touchend: null,
                touchcancel: null,
                mousedown: null,
                mousemove: null,
                mouseup: null,
                mouseleave: null,
                focusTouchstart: null,
                focusMouseenter: null,
                focusMouseleave: null,
            },
        };
    },
    methods: {
        ...mapActions('glb', ['getDppPhotoByUrl']),
        observerCardProperty() {
            if (!observerCardProperty) {
                let fn = throttle((entries, observer) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            entry.target?.__vue__?.initPhotoUrls(true);
                            observer.unobserve(entry.target);
                        }
                    });
                }, 20);
                observerCardProperty = new IntersectionObserver(fn, { rootMargin: '300px 0px', threshold: 0 });
            }
            observerCardProperty.observe(this.$el);
        },
        initialDrag() {
            if (this.initaledDrag) {
                return;
            }
            this.initaledDrag = true;
            let $list = this.$refs.photoContainer;
            if (!$list) {
                return;
            }

            let width;
            let timer;
            let touchStart;
            let touchStartY;
            let deltaX = 0;

            let reset = () => {
                if ($list.style.transition) {
                    $list.style.transition = 'none';
                }
                if (deltaX > this.sensitivity) {
                    // throttle threshold
                    this._prevPhoto();
                } else if (deltaX < 0 && deltaX < this.sensitivity * -1) {
                    this._nextPhoto();
                }
                $list.style.transform = 'translateX(0px)';
                deltaX = 0;
                // Handle touch end for click detection on touch devices
                this.isDragging = false;
            };

            let isTouchScreen = window.ontouchstart !== undefined;
            let onStart = isTouchScreen
                ? (e) => {
                      let touch = e.touches[0];
                      clearTimeout(timer);
                      width = $list.clientWidth;
                      touchStart = touch.clientX;
                      touchStartY = touch.clientY;
                      if (e.touches.length > 1) {
                          return;
                      }
                      reset();
                  }
                : (e) => {
                      clearTimeout(timer);
                      width = $list.clientWidth;
                      reset();
                      touchStart = e.clientX;
                  };

            let onMove = isTouchScreen
                ? (e) => {
                      if ($list.style.transition) {
                          $list.style.transition = 'none';
                      }

                      cancelAnimationFrame(timer);
                      timer = requestAnimationFrame(() => {
                          let touch = e.touches[0];
                          deltaX = touch.clientX - touchStart;
                          if (Math.abs(deltaX) > 5) {
                              this.isDragging = true;
                              this.initPhotoUrls();
                          }
                          $list.style.transform = `translateX(${deltaX}px)`;
                      });
                  }
                : (e) => {
                      if (!touchStart) {
                          return;
                      }
                      if ($list.style.transition) {
                          $list.style.transition = 'none';
                      }

                      cancelAnimationFrame(timer);
                      timer = requestAnimationFrame(() => {
                          deltaX = e.clientX - touchStart;
                          if (Math.abs(deltaX) > 5) {
                              this.isDragging = true;
                          }
                          $list.style.transform = `translateX(${deltaX}px)`;
                      });
                  };

            let onEnd = isTouchScreen
                ? (e) => {
                      if (!touchStart) {
                          return;
                      }
                      touchStart = null;
                      if (e.touches.length > 0) {
                          return;
                      }
                      cancelAnimationFrame(timer);
                      width = $list.clientWidth;
                      $list.style.transition = `transform 0.3s`;

                      if (deltaX > 0 && deltaX > this.sensitivity && this.isDragging) {
                          // snap to left with animation next
                          $list.style.transform = `translateX(${width}px)`;
                      } else if (deltaX < 0 && deltaX < this.sensitivity * -1) {
                          // snap to right with animation prev
                          $list.style.transform = `translateX(${width * -1}px)`;
                      } else {
                          $list.style.transform = 'translateX(0px)';
                          if (Math.abs(deltaX) < 1) {
                              this.isDragging = false;
                              this.emitOpenDpp();
                          }
                      }

                      timer = setTimeout(() => {
                          reset();
                      }, 330);
                  }
                : () => {
                      if (!touchStart) {
                          return;
                      }
                      touchStart = null;
                      cancelAnimationFrame(timer);
                      width = $list.clientWidth;
                      $list.style.transition = `transform 0.3s`;

                      if (deltaX > 0 && deltaX > 50) {
                          // snap to left with animation next
                          $list.style.transform = `translateX(${width}px)`;
                      } else if (deltaX < 0 && deltaX < -50) {
                          // snap to right with animation prev
                          $list.style.transform = `translateX(${width * -1}px)`;
                      } else {
                          $list.style.transform = 'translateX(0px)';
                      }
                      timer = setTimeout(() => {
                          reset();
                      }, 330);
                  };

            if (isTouchScreen) {
                this.eventHandlers.touchstart = (e) => {
                    onStart(e);
                };
                this.eventHandlers.touchmove = (e) => {
                    // Only trigger on horizontal move
                    if (e.touches.length === 1 && touchStart && touchStartY) {
                        const dx = Math.abs(e.touches[0].clientX - touchStart);
                        const dy = Math.abs(e.touches[0].clientY - touchStartY);
                        if (dx > dy) {
                            e.preventDefault();
                            onMove(e);
                        } else {
                            touchStart = null;
                            touchStartY = null;
                        }
                    }
                };
                this.eventHandlers.touchend = (e) => {
                    if (e.cancelable) {
                        e.preventDefault();
                    }
                    onEnd(e);
                };
                this.eventHandlers.touchcancel = (e) => {
                    if (e.cancelable) {
                        e.preventDefault();
                    }
                    onEnd(e);
                };

                $list.addEventListener('touchstart', this.eventHandlers.touchstart);
                $list.addEventListener('touchmove', this.eventHandlers.touchmove);
                $list.addEventListener('touchend', this.eventHandlers.touchend);
                $list.addEventListener('touchcancel', this.eventHandlers.touchcancel);
            } else {
                this.eventHandlers.mousedown = (e) => {
                    e.preventDefault();
                    onStart(e);
                };
                this.eventHandlers.mousemove = (e) => {
                    e.preventDefault();
                    onMove(e);
                };
                this.eventHandlers.mouseup = (e) => {
                    e.preventDefault();
                    onEnd(e);
                };
                this.eventHandlers.mouseleave = (e) => {
                    e.preventDefault();
                    onEnd(e);
                };

                $list.addEventListener('mousedown', this.eventHandlers.mousedown);
                $list.addEventListener('mousemove', this.eventHandlers.mousemove);
                $list.addEventListener('mouseup', this.eventHandlers.mouseup);
                $list.addEventListener('mouseleave', this.eventHandlers.mouseleave);
            }
        },
        removeEventListeners() {
            const $list = this.$refs.photoContainer;
            if (!$list) return;

            // Remove drag event listeners
            if (this.eventHandlers.touchstart) {
                $list.removeEventListener('touchstart', this.eventHandlers.touchstart);
                this.eventHandlers.touchstart = null;
            }
            if (this.eventHandlers.touchmove) {
                $list.removeEventListener('touchmove', this.eventHandlers.touchmove);
                this.eventHandlers.touchmove = null;
            }
            if (this.eventHandlers.touchend) {
                $list.removeEventListener('touchend', this.eventHandlers.touchend);
                this.eventHandlers.touchend = null;
            }
            if (this.eventHandlers.touchcancel) {
                $list.removeEventListener('touchcancel', this.eventHandlers.touchcancel);
                this.eventHandlers.touchcancel = null;
            }
            if (this.eventHandlers.mousedown) {
                $list.removeEventListener('mousedown', this.eventHandlers.mousedown);
                this.eventHandlers.mousedown = null;
            }
            if (this.eventHandlers.mousemove) {
                $list.removeEventListener('mousemove', this.eventHandlers.mousemove);
                this.eventHandlers.mousemove = null;
            }
            if (this.eventHandlers.mouseup) {
                $list.removeEventListener('mouseup', this.eventHandlers.mouseup);
                this.eventHandlers.mouseup = null;
            }
            if (this.eventHandlers.mouseleave) {
                $list.removeEventListener('mouseleave', this.eventHandlers.mouseleave);
                this.eventHandlers.mouseleave = null;
            }

            // Remove focus event listeners
            if (this.eventHandlers.focusTouchstart) {
                this.$el.removeEventListener('touchstart', this.eventHandlers.focusTouchstart);
                this.eventHandlers.focusTouchstart = null;
            }
            if (this.eventHandlers.focusMouseenter) {
                this.$el.removeEventListener('mouseenter', this.eventHandlers.focusMouseenter);
                this.eventHandlers.focusMouseenter = null;
            }
            if (this.eventHandlers.focusMouseleave) {
                this.$el.removeEventListener('mouseleave', this.eventHandlers.focusMouseleave);
                this.eventHandlers.focusMouseleave = null;
            }

            // Remove event bus listeners
            this.$eventBus.$off('card-focus-in');

            // Clear timers
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = null;
            }
        },
        onClick(e) {
            // Don't trigger click if we were dragging
            if (this.isDragging) {
                this.isDragging = false;
                return;
            }
            e.stopPropagation();
            e.preventDefault();
            this.emitOpenDpp();
        },
        emitOpenDpp() {
            this.$emit('openDpp', this.index);
            if (this.property.photoCount > 0 && this.viewedCount > 0) {
                this.$eventBus.$emit('ga', { en: 'photo_viewed', ec: 'mvtCardProperty', el: Math.min(this.viewedCount, this.property.photoCount) });
            }
        },
        onFocusIn() {
            if (this.isFocusMode) return;
            this.isFocusMode = true;
            this.$eventBus.$emit('card-focus-in', this.property.propertyId);
        },
        async initPhotoUrls() {
            if (this.loading === 0) {
                this.loading = 1;
                this.photoUrls = new Array(this.property.photoCount).fill('');
                this.photoUrls[0] = this.property.tnImgPath;
                try {
                    let photos = await this.fetchListingDetails();
                    for (let i = 1; i < photos.length; i++) {
                        this.photoUrls[i] = photos[i];
                    }
                    this.cachedPhoto();
                    this.loading = 2;
                    // Create a promise that resolves when both images are loaded
                    return true;
                } catch (err) {
                    console.error('Error loading photo', err);
                    this.photoUrls = [this.property.tnImgPath];
                    this.loading = 2;
                    return false;
                }
            } else {
                return true;
            }
        },
        cachedPhoto() {
            if (this.buffer === 0 || this.photoUrls.length < 2) {
                return;
            }
            this.precachePhoto(this.index);
            //base on this.index, get the precache photos
            for (let i = 1; i <= this.buffer; i++) {
                //precache the ${i}th next photo
                if (this.index + i < this.getPhotoCount) {
                    this.precachePhoto(this.index + i);
                } else if (this.index + i >= this.getPhotoCount) {
                    this.precachePhoto(this.index + i - this.getPhotoCount);
                }
                //precache only one prev photo
                if (i === 1) {
                    if (this.index - i >= 0) {
                        this.precachePhoto(this.index - i);
                    } else if (this.index - i < 0) {
                        this.precachePhoto(this.index - i + this.getPhotoCount);
                    }
                }
            }
        },
        precachePhoto(index) {
            let url = this.photoUrls[index];
            if (!url) {
                return;
            }
            let status = getImgStatus(url);
            if (status !== ImgStatus.UNLOAD) {
                return;
            }
            //set the status to loading
            this.photoUrls[index] = getImgStatusUrl(url, ImgStatus.LOADING);
            let img = new Image();
            //preload the image and will be auto cached by browser
            img.src = getImgStatusUrl(url, ImgStatus.LOADED);
            img.onload = () => {
                //success, set the status to loaded
                this.photoUrls[index] = getImgStatusUrl(url, ImgStatus.LOADED);
            };
            img.onerror = () => {
                //failed, set the status to unload
                this.photoUrls[index] = getImgStatusUrl(url, ImgStatus.UNLOAD);
            };
        },
        async fetchListingDetails() {
            const { listingId: id, path: url, photoCount } = this.property;
            if (photoCount <= 1) return [];

            try {
                const dppPhoto = await this.getDppPhotoByUrl({ url });
                let photos = Array.isArray(dppPhoto)
                    ? dppPhoto.reduce((acc, curr) => {
                          if (curr.photos && curr.photos.length > 0) {
                              let urls = curr.photos.map((e) => {
                                  let url = e.url;
                                  if (/.webp$/.test(url)) {
                                      url = url.replace('.webp', '_p.webp');
                                  }
                                  return getImgStatusUrl(url, ImgStatus.UNLOAD);
                              });
                              acc = acc.concat(urls);
                          }
                          return acc;
                      }, [])
                    : [];

                return photos.length > 0 ? photos : [];
            } catch (err) {
                console.error(`Error fetching photos for property: ${id}`, err);
                return [];
            }
        },
        onAnimationEnd() {
            if (this.animationName === 'moving-next') {
                this._nextPhoto();
            } else if (this.animationName === 'moving-prev') {
                this._prevPhoto();
            }
        },
        _nextPhoto() {
            this.viewedCount++;
            this.prevIndex = this.index;
            this.index = this.nextIndex;
            if (this.nextIndex < this.getPhotoCount - 1) {
                this.nextIndex = this.nextIndex + 1;
            } else {
                this.nextIndex = 0;
            }
            this.animationName = '';
            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.cachedPhoto();
            }, 150);
        },
        _prevPhoto() {
            this.viewedCount++;
            this.nextIndex = this.index;
            this.index = this.prevIndex;
            if (this.prevIndex > 0) {
                this.prevIndex = this.prevIndex - 1;
            } else {
                this.prevIndex = this.getPhotoCount - 1;
            }
            this.animationName = '';
            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.cachedPhoto();
            }, 150);
        },
        async nextPhoto(e) {
            e.stopPropagation();
            e.preventDefault();
            if (this.photoUrls.length <= 1) {
                return;
            }
            await this.initPhotoUrls();
            if (this.animationName) {
                return;
            }
            this.animationName = 'moving-next';
        },
        async prevPhoto(e) {
            e.stopPropagation();
            e.preventDefault();
            if (this.photoUrls.length <= 1) {
                return;
            }
            await this.initPhotoUrls();
            if (this.animationName) {
                return;
            }
            this.animationName = 'moving-prev';
        },
    },
    mounted() {
        if (this.disablePreviewPhoto) return;
        let isTouchScreen = window.ontouchstart !== undefined;
        if (isTouchScreen) {
            this.$eventBus.$on('card-focus-in', (propertyId) => {
                if (this.isFocusMode && propertyId !== this.property.propertyId) {
                    this.isFocusMode = false;
                }
            });
            this.eventHandlers.focusTouchstart = () => {
                this.onFocusIn();
            };
            this.$el.addEventListener('touchstart', this.eventHandlers.focusTouchstart);
            this.initialDrag();
            //check if current photo is on viewport, need keep watching after scrolled
        } else {
            this.eventHandlers.focusMouseenter = () => {
                //This is to prevent multiple calls to server when user hovers rapidly over more than 1 card.
                this.onFocusIn();
            };
            this.eventHandlers.focusMouseleave = () => {
                this.isFocusMode = false;
            };
            this.$el.addEventListener('mouseenter', this.eventHandlers.focusMouseenter);
            this.$el.addEventListener('mouseleave', this.eventHandlers.focusMouseleave);
            // this.initialDrag();
        }
        if (this.glb.isPhone) {
            this.observerCardProperty();
        }
        this.loaded = true;
    },
    beforeUnmount() {
        if (observerCardProperty) {
            observerCardProperty?.unobserve(this.$el);
        }
        this.removeEventListeners();
    },
};
